import type { ACTION, SecuritiesContextProps } from '@talos/kyoko';
import type { FeatureFlags } from '../../../hooks/useFeatureFlag';
import type { LayoutSection, LayoutSpec } from '../../../types/LayoutConfig';
import { MONITORING_CONDITIONAL_LAYOUT_SPEC_RULES } from './Monitoring/conditionalLayoutSpecs';
import { TRADING_CONDITIONAL_LAYOUT_SPEC_RULES } from './Trading/conditionalLayoutSpecs';

export interface LayoutSpecVisibilityConditions {
  flags: FeatureFlags;
  isAuthorized: (permission: ACTION) => boolean;
  securities: SecuritiesContextProps;
}

export interface ConditionalLayoutSpecRule {
  layoutSpec: LayoutSpec;
  isVisible: (conditions: LayoutSpecVisibilityConditions) => boolean;
}

// Keep conditional visibility rules separate from LayoutSpec itself.
// Layout specs are cloned and treated like serializable config data in multiple places,
// so function-based visibility checks live in this sidecar registry instead.
const CONDITIONAL_LAYOUT_SPEC_RULES: Record<LayoutSection, Record<string, ConditionalLayoutSpecRule>> = {
  trading: TRADING_CONDITIONAL_LAYOUT_SPEC_RULES,
  monitoring: MONITORING_CONDITIONAL_LAYOUT_SPEC_RULES,
};

function getTemplateId(layoutSpec: LayoutSpec) {
  return layoutSpec.templateId ?? layoutSpec.id;
}

export function isConditionalLayoutSpecVisible(
  layoutSection: LayoutSection,
  layoutSpec: LayoutSpec,
  conditions: LayoutSpecVisibilityConditions
) {
  const rule = CONDITIONAL_LAYOUT_SPEC_RULES[layoutSection][getTemplateId(layoutSpec)];

  return rule == null || rule.isVisible(conditions);
}

export function getVisibleConditionalDefaultLayoutSpecs(
  layoutSection: LayoutSection,
  conditions: LayoutSpecVisibilityConditions
) {
  return Object.values(CONDITIONAL_LAYOUT_SPEC_RULES[layoutSection])
    .filter(rule => rule.isVisible(conditions))
    .map(rule => rule.layoutSpec);
}
